import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, PackageIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { LogoMark } from '../components/LogoMark';
import { FieldNotes } from '../sections/FieldNotes';
import { RESOURCES } from '../data/site';
interface ResourcesProps {
  onDownload: (title?: string) => void;
}
export function Resources({
  onDownload
}: ResourcesProps) {
  return <>
      <section className="relative w-full overflow-hidden bg-cream py-16 lg:py-24" data-id="element-384">
        <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-385" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8" data-id="element-386">
          <div className="max-w-3xl" data-id="element-387">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-388">
              <LogoMark className="h-3.5 w-3.5" data-id="element-389" /> Resource library
            </p>
            <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl" data-id="element-390">
              Free Mine Geology Systems Resources
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-warmgray" data-id="element-391">
              Download the free MGS Toolkit today — four practical tools for
              assessing, reviewing and improving your mine geology system, in
              one download.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-sand py-20 lg:py-28" data-id="element-392">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-393">
          <div className="overflow-hidden rounded-2xl border border-charcoal/10 bg-charcoal shadow-card" data-id="element-394">
            <div className="grid gap-8 p-7 lg:grid-cols-12 lg:items-center lg:p-12" data-id="element-395">
              <div className="lg:col-span-8" data-id="element-396">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-yellow" data-id="element-397">
                  <PackageIcon className="h-4 w-4" data-id="element-398" /> The MGS Toolkit
                </p>
                <h2 className="mt-3 text-3xl font-black leading-tight text-cream sm:text-4xl" data-id="element-399">
                  One download. A complete system review kit.
                </h2>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-cream/80" data-id="element-400">
                  The System Overview, monthly Checklist, Monthly System Review
                  and Yearly Scorecard Tracker — built to work together as a
                  continuous improvement rhythm for your geology system.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right" data-id="element-401">
                <Button variant="primary" onClick={() => onDownload()} data-id="element-402">
                  <DownloadIcon className="h-4 w-4" data-id="element-403" />
                  Download the MGS Toolkit
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-10 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-404">
            {RESOURCES.map((r, i) => <motion.div key={r.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.35,
            delay: i * 0.05
          }} className="flex h-full flex-col rounded-xl border border-charcoal/10 bg-cream p-6 shadow-card transition-transform duration-200 hover:-translate-y-1" data-id="element-405">
                <div className="flex items-center justify-between" data-id="element-406">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{
                color: r.categoryColor
              }} data-id="element-407">
                    <span className="h-2 w-2 rounded-sm" style={{
                  backgroundColor: r.categoryColor
                }} data-id="element-408" />
                    {r.type}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-improvements/15 px-2.5 py-1 text-xs font-bold text-improvements" data-id="element-409">
                    <DownloadIcon className="h-3 w-3" data-id="element-410" />
                    In the Toolkit
                  </span>
                </div>
                <h2 className="mt-4 text-lg font-bold leading-snug text-charcoal" data-id="element-411">
                  {r.title}
                </h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-warmgray" data-id="element-412">
                  {r.description}
                </p>
                <button type="button" onClick={() => onDownload()} className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-yellow px-4 py-2.5 text-sm font-bold text-charcoal transition-colors hover:brightness-95" data-id="element-413">
                  <DownloadIcon className="h-4 w-4" data-id="element-414" />
                  Get it in the Toolkit
                </button>
              </motion.div>)}
          </div>
          <p className="mt-8 text-center text-xs text-warmgray" data-id="element-415">
            More resources are coming as the library grows — have a suggestion?
            Send it via the contact page.
          </p>
        </div>
      </section>

      <FieldNotes data-id="element-416" />

      <section className="w-full bg-cream py-20 lg:py-24" data-id="element-417">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8" data-id="element-418">
          <h2 className="text-3xl font-black leading-tight text-charcoal sm:text-4xl" data-id="element-419">
            New here? Start with the framework.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-warmgray" data-id="element-420">
            Every tool in the Toolkit maps to a part of the MGS framework.
            Understanding the system first makes every tool more useful.
          </p>
          <Button to="/framework" variant="secondary" className="mt-8" data-id="element-421">
            Explore the Framework
          </Button>
        </div>
      </section>
    </>;
}